import { useEffect } from "react"
import { clearCompletionInboxResource } from "@/entities/notification/completion-inbox"
import {
  clearReviewResourceCompletions,
  watchReviewCompletion,
} from "@/entities/notification/completion-alerts"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  createReview,
  deleteReview,
  evaluateTarget,
  getReview,
  listReviews,
} from "./review.api"
import { anyRunning, withTarget } from "./review.lib"
import type { Review, ReviewDetail, ReviewTarget } from "./review.types"

/** How often a review with something running is re-read, stream or no stream. */
const RUNNING_REFETCH_MS = 10_000

export const reviewKeys = {
  all: () => ["assistant", "reviews"] as const,
  list: (pageSize?: number, offset?: number) =>
    ["assistant", "reviews", "list", pageSize ?? null, offset ?? null] as const,
  detail: (reviewId: string) => ["assistant", "reviews", reviewId] as const,
}

export const useReviewsQuery = ({
  pageSize,
  offset,
  enabled = true,
}: {
  pageSize?: number
  offset?: number
  enabled?: boolean
} = {}) =>
  useQuery({
    queryKey: reviewKeys.list(pageSize, offset),
    queryFn: () => listReviews({ pageSize, offset }),
    enabled,
  })

export const useReviewQuery = ({
  reviewId,
  enabled = true,
}: {
  reviewId: string
  enabled?: boolean
}) => {
  const query = useQuery({
    queryKey: reviewKeys.detail(reviewId),
    queryFn: () => getReview({ reviewId }),
    enabled,
    // The server's word, every time the page is opened.
    //
    // The app-wide three-minute staleTime served this page its cached detail on return — targets
    // still "pending" — while the server was already evaluating them, and the row's Evaluate
    // button came back over a run that was still going. What this tab had started was component
    // state, gone with the navigation.
    staleTime: 0,
    refetchOnMount: "always",
    // A belt under the stream. While anything runs the row is re-read regardless, which is what
    // moves a row to done when its stream could not be followed — a second replica, a proxy that
    // closed it. One small request every ten seconds, and only while something runs.
    refetchInterval: (query) =>
      query.state.data && anyRunning(query.state.data.targets)
        ? RUNNING_REFETCH_MS
        : false,
  })

  useEffect(() => {
    if (!enabled || !query.data) return
    query.data.targets
      .filter((target) => target.status === "in_progress")
      .forEach((target) => {
        watchReviewCompletion({
          kind: "review",
          resourceId: reviewId,
          runId: target.id,
          startedAt: target.updateDt,
        })
      })
  }, [enabled, reviewId, query.data])
  return query
}

/**
 * The whole `reviews` subtree, not one key.
 *
 * A review's list row carries what its detail changes — the same mistake the chat sidebar made,
 * where invalidating `conversation(id)` left the list stale because it is a sibling key rather
 * than a descendant.
 */
const useInvalidateReviews = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: reviewKeys.all() })
}

export const useCreateReviewMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (review: Review) => void
  /** Receives the request that failed, so a caller can word the error by what it sent. */
  onError?: (
    error: unknown,
    request: Parameters<typeof createReview>[0]
  ) => void
} = {}) => {
  const invalidate = useInvalidateReviews()
  return useMutation({
    mutationFn: createReview,
    onSuccess: async (review) => {
      await invalidate()
      onSuccess?.(review)
    },
    onError: (error, request) => onError?.(error, request),
  })
}

export const useDeleteReviewMutation = ({
  onSuccess,
  onError,
}: {
  /** Receives the variables this completion belongs to, so a caller can tell two deletes apart. */
  onSuccess?: (deleted: { reviewId: string }) => void
  onError?: (error: unknown) => void
} = {}) => {
  const invalidate = useInvalidateReviews()
  return useMutation({
    mutationFn: deleteReview,
    onSuccess: async (_data, variables) => {
      clearReviewResourceCompletions("review", variables.reviewId)
      clearCompletionInboxResource("review", variables.reviewId)
      await invalidate()
      onSuccess?.(variables)
    },
    onError,
  })
}

export const useEvaluateTargetMutation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (target: ReviewTarget) => void
  /** Receives the variables this failure belongs to — several targets can be running at once. */
  onError?: (
    error: unknown,
    variables: { reviewId: string; targetId: string }
  ) => void
} = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: evaluateTarget,
    onSuccess: (target) => {
      if (target.status === "in_progress") {
        watchReviewCompletion({
          kind: "review",
          resourceId: target.reviewId,
          runId: target.id,
          startedAt: target.updateDt,
        })
      }
      // The claim goes straight into the cache. The server answers with the row as it now stands
      // — `in_progress` — and that is what the stream hook watches for; waiting on a refetch would
      // leave the button on screen for another round trip.
      queryClient.setQueryData<ReviewDetail>(
        reviewKeys.detail(target.reviewId),
        (detail) => detail && withTarget(detail, target)
      )
      onSuccess?.(target)
    },
    onError: (error, variables) => onError?.(error, variables),
  })
}
